import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { MultiplierTypeQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { MultiplierTypeFilters, MultiplierTypeOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { EditDialog } from './edit_dialog'
import { pluralize } from '@/helpers/pluralize'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<MultiplierTypeQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const {
    pageInfo,
    setOpenNew,
    openShow,
    openNew,
    showShow,
    hideShow,
    resourceId,
  } = useTable({ pageInfo: data?.multiplierTypes.pageInfo, onIndex, basePath: '/settings/charge-multipliers' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<MultiplierTypeFilters, MultiplierTypeOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.multiplierTypes.nodes = dedupe([
              ...previousData.multiplierTypes.nodes,
              ...fetchMoreResult.multiplierTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Hire duration' },
          { name: 'Charge duration' },
        ]}
        rows={data.multiplierTypes.nodes.map(mt => (
          {
            id: mt.id,
            columns: [
              mt.name,
              pluralize(mt.operandType, mt.operandQuantity, true),
              pluralize(mt.multiplierType, mt.multiplier, true)
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <EditDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
